<template>
  <div class="w-full">
    <div class="flex items-center gap-2 group">
      <button 
        @click="openPicker" 
        type="button"
        class="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 transition-all shadow-sm active:scale-95 whitespace-nowrap"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        {{ buttonText }}
      </button>
    </div>

    <Teleport to="body">
      <div v-if="isOpen" class="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300" @click="closePicker"></div>
        
        <div class="relative bg-white w-full max-w-5xl h-[80vh] rounded-[2rem] shadow-2xl border border-white flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
          
          <div class="px-8 py-6 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-bold text-slate-900 leading-none">Médiathèque</h2>
              <p class="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-2">Assets & Ressources</p>
            </div>
            <button @click="closePicker" class="p-2 hover:bg-slate-100 rounded-full transition-colors">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div class="px-8 bg-slate-50/50 flex gap-8 border-b border-slate-100">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="['py-4 text-xs font-black uppercase tracking-widest border-b-2 transition-all', activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600']"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="flex-1 overflow-hidden flex flex-col p-8">
<div v-if="activeTab === 'library'" class="flex-1 flex flex-col gap-6 overflow-hidden">
  <div class="flex flex-wrap gap-4 items-center justify-between">
    <div class="flex gap-3 flex-1 max-w-md">
      <div class="relative flex-1">
        <input v-model="searchQuery" @input="debounceSearch" placeholder="Rechercher un fichier..." class="w-full bg-slate-100 border-none rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all" />
        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>
      <select v-model="selectedFolder" class="bg-slate-100 border-none rounded-xl px-4 py-2.5 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500/20">
        <option value="all">Tous les dossiers</option>
        <option v-for="folder in folders" :key="folder.name" :value="folder.name">📁 {{ folder.name }}</option>
      </select>
    </div>
    <div class="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
      {{ pagination.total || 0 }} fichiers trouvés
    </div>
  </div>

  <div class="flex-1 overflow-y-auto pr-2 custom-scrollbar">
    <div v-if="loading" class="h-full flex flex-col items-center justify-center py-20">
      <div class="w-8 h-8 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
    </div>

    <div v-else-if="media.length > 0" class="grid  md:grid-cols-4 lg:grid-cols-5 gap-6">
      <div
        v-for="item in media"
        :key="item.id || item.url"
        @click="selectMedia(item)"
        :class="[
          'group relative aspect-square rounded-2xl overflow-hidden cursor-pointer border-2 transition-all', 
          (selectedMedia && selectedMedia.url === item.url) 
            ? 'border-indigo-600 ring-4 ring-indigo-500/10' 
            : 'border-slate-100 hover:border-indigo-300 shadow-sm'
        ]"
      >
        <img :src="item.url" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        
        <div class="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
          <p class="text-[10px] font-bold text-white truncate">{{ item.name }}</p>
          <p class="text-[8px] text-slate-300 uppercase font-black">{{ item.readableSize }}</p>
        </div>

        <div 
          v-if="selectedMedia && selectedMedia.url === item.url" 
          class="absolute top-2 right-2 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs shadow-lg animate-in zoom-in duration-200"
        >
          ✓
        </div>
      </div>
    </div>

    <div v-else class="h-full flex flex-col items-center justify-center opacity-40 py-20">
      <span class="text-4xl mb-4">📂</span>
      <p class="text-sm font-bold uppercase tracking-widest text-slate-500">Aucun média trouvé</p>
    </div>
  </div>
</div>

            <div v-if="activeTab === 'upload'" class="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto w-full">
              <div
                class="w-full border-2 border-dashed rounded-[2.5rem] p-12 text-center transition-all cursor-pointer"
                :class="isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-400 hover:bg-slate-50/50'"
                @drop.prevent="handleDrop"
                @dragover.prevent="isDragging = true"
                @dragleave="isDragging = false"
                @click="$refs.fileInput.click()"
              >
                <input ref="fileInput" type="file" accept="image/*" @change="handleFileSelect" class="hidden" :multiple="allowMultiple" />
                <div class="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                </div>
                <h3 class="text-lg font-bold text-slate-900 mb-2">Déposez vos médias ici</h3>
                <p class="text-slate-400 text-sm mb-6">Ou cliquez pour parcourir vos fichiers localement</p>
                <div class="flex gap-2 justify-center">
                   <span class="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-400 uppercase">PNG</span>
                   <span class="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-400 uppercase">JPG</span>
                   <span class="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-400 uppercase">WEBP</span>
                </div>
              </div>

              <div class="mt-8 flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 w-full">
                <span class="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">Dossier cible</span>
                <input v-model="uploadFolder" placeholder="root" class="flex-1 bg-transparent border-none text-sm font-bold text-indigo-600 outline-none placeholder:text-slate-300" />
              </div>
            </div>

            <div v-if="activeTab === 'url'" class="flex-1 flex flex-col items-center justify-center max-w-xl mx-auto w-full">
                <div class="w-full space-y-4">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Lien direct vers l'image</label>
                  <input v-model="directUrl" placeholder="https://cloud.com/mon-image.png" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-5 text-sm font-medium focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none" />
                  <button @click="useDirectUrl" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-600 transition-all">Importer via URL</button>
                </div>
            </div>

          </div>

          <div class="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
            <div v-if="activeTab === 'library' && pagination.pages > 1" class="flex items-center gap-2">
              <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1" class="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30">←</button>
              <span class="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-4">Page {{ pagination.page }} / {{ pagination.pages }}</span>
              <button @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.pages" class="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-30">→</button>
            </div>
            <div v-else></div>

            <div class="flex gap-4">
              <button @click="closePicker" class="px-6 py-2.5 text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600">Annuler</button>
              <button
                v-if="selectedMedia"
                @click="confirmSelection"
                class="px-8 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
              >
                Confirmer la sélection
              </button>
            </div>
          </div>

          <div v-if="uploading" class="absolute inset-0 bg-white/80 backdrop-blur-sm z-[20] flex flex-col items-center justify-center p-12">
              <div class="w-full max-w-md">
                <div class="flex justify-between mb-2">
                  <span class="text-xs font-black uppercase text-indigo-600">Envoi en cours...</span>
                  <span class="text-xs font-black text-indigo-600">{{ uploadProgress }}%</span>
                </div>
                <div class="h-2 w-full bg-indigo-100 rounded-full overflow-hidden">
                  <div class="h-full bg-indigo-600 transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from 'vue';
import { toast } from 'vue3-toastify';

// Props
const props = defineProps({
  modelValue: String,
  placeholder: { type: String, default: 'URL de l\'image...' },
  buttonText: { type: String, default: 'Choisir une image' },
  showUrlInput: { type: Boolean, default: true },
  allowMultiple: { type: Boolean, default: false },
  agencyId: String
});

// Emits
const emit = defineEmits(['update:modelValue', 'media-selected']);

// State
const isOpen = ref(false);
const activeTab = ref('library');
const tabs = [
  { id: 'library', label: 'Bibliothèque', icon: '📚' },
  { id: 'upload', label: 'Upload', icon: '⬆️' },
  { id: 'url', label: 'URL directe', icon: '🔗' }
];

// Upload state
const fileInput = ref(null);
const uploading = ref(false);
const uploadProgress = ref(0);
const uploadFolder = ref('root');
const isDragging = ref(false);

// Library state
const media = ref([]);
const folders = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const selectedFolder = ref('all');
const selectedMedia = ref(null);
const pagination = ref({ page: 1, pages: 1, total: 0, limit: 20 });

// URL state
const directUrl = ref('');

// Search debounce
let searchTimeout = null;
const debounceSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchMedia();
  }, 500);
};

// Methods
const openPicker = () => {
  isOpen.value = true;
  fetchMedia();
  fetchFolders();
};

const closePicker = () => {
  isOpen.value = false;
  selectedMedia.value = null;
  directUrl.value = '';
};

const fetchMedia = async () => {
  loading.value = true;
  try {
    const token = localStorage.getItem('superAdminToken');
    
    // OPTION A : On ne filtre plus par agencyId pour voir TOUTES les images
    const queryParams = {
      page: pagination.value.page,
      limit: pagination.value.limit,
      folder: selectedFolder.value,
    };

    if (searchQuery.value) queryParams.search = searchQuery.value;
    
    // On ignore props.agencyId ici pour le listage
    const params = new URLSearchParams(queryParams);

    const res = await fetch(`https://api.datafuse.fr/api/super-admin/media/list?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      const data = await res.json();
      media.value = data.media;
      pagination.value = data.pagination;
    } else {
      toast.error('Erreur de chargement');
    }
  } catch (err) {
    console.error('Erreur fetch media:', err);
    toast.error('Erreur réseau');
  } finally {
    loading.value = false;
  }
};

const fetchFolders = async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const params = new URLSearchParams({
      ...(props.agencyId && { agencyId: props.agencyId })
    });

    const res = await fetch(`https://api.datafuse.fr/api/super-admin/media/folders?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      const data = await res.json();
      folders.value = data.folders;
    }
  } catch (err) {
    console.error('Erreur fetch folders:', err);
  }
};

const handleFileSelect = async (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    await uploadFiles(files);
  }
};

const handleDrop = async (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    await uploadFiles(files);
  }
};

const uploadFiles = async (files) => {
  uploading.value = true;
  uploadProgress.value = 0;

  try {
    const token = localStorage.getItem('superAdminToken');
    const formData = new FormData();

    if (files.length === 1) {
      formData.append('image', files[0]);
      formData.append('name', files[0].name);
    } else {
      for (let file of files) {
        formData.append('images', file);
      }
    }

    formData.append('folder', uploadFolder.value || 'root');
    if (props.agencyId) {
      formData.append('agencyId', props.agencyId);
    }

    const endpoint = files.length === 1 ? '/upload' : '/upload-batch';

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        uploadProgress.value = Math.round((e.loaded / e.total) * 100);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 201) {
        const data = JSON.parse(xhr.responseText);
        toast.success(data.message);

        // Si single upload, sélectionner automatiquement
        if (files.length === 1 && data.media) {
          emit('update:modelValue', data.media.url);
          closePicker();
        } else {
          // Refresh library
          fetchMedia();
          fetchFolders();
          activeTab.value = 'library';
        }
      } else {
        toast.error('Erreur d\'upload');
      }
      uploading.value = false;
    });

    xhr.addEventListener('error', () => {
      toast.error('Erreur réseau');
      uploading.value = false;
    });

    xhr.open('POST', `https://api.datafuse.fr/api/super-admin/media${endpoint}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);

  } catch (err) {
    console.error('Erreur upload:', err);
    toast.error('Erreur d\'upload');
    uploading.value = false;
  }
};

const selectMedia = (item) => {
  selectedMedia.value = item;
};

const confirmSelection = () => {
  if (selectedMedia.value) {
    emit('update:modelValue', selectedMedia.value.url);
    emit('media-selected', selectedMedia.value);
    closePicker();
    toast.success('Image sélectionnée');
  }
};

const useDirectUrl = () => {
  if (directUrl.value.trim()) {
    emit('update:modelValue', directUrl.value.trim());
    closePicker();
    toast.success('URL ajoutée');
  }
};

const changePage = (page) => {
  pagination.value.page = page;
  fetchMedia();
};

// Charger les données au montage du composant
onMounted(() => {
  fetchMedia();
  fetchFolders();
});

// Recharger automatiquement quand on change de dossier
watch(selectedFolder, () => {
  pagination.value.page = 1;
  fetchMedia();
});

// Recharger si l'agencyId change dynamiquement
watch(() => props.agencyId, () => {
  fetchMedia();
  fetchFolders();
});
</script>

<style scoped>
.media-picker {
  width: 100%;
}

.input-group {
  display: flex;
  gap: 8px;
}

.url-input {
  flex: 1;
  padding: 10px;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 13px;
}

.picker-button {
  padding: 10px 16px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.picker-button:hover {
  background: #2563EB;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 2px solid #F1F5F9;
}

.modal-title {
  font-size: 24px;
  font-weight: 800;
  color: #1E293B;
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #F1F5F9;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.close-button:hover {
  background: #E2E8F0;
  transform: scale(1.1);
}

/* Tabs */
.tabs {
  display: flex;
  gap: 4px;
  padding: 16px 24px 0;
  border-bottom: 2px solid #F1F5F9;
}

.tab {
  padding: 12px 20px;
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 14px;
  color: #64748B;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
  margin-bottom: -2px;
}

.tab:hover {
  color: #3B82F6;
}

.tab.active {
  color: #3B82F6;
  border-bottom-color: #3B82F6;
}

/* Tab Content */
.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

/* Upload Section */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.drop-zone {
  border: 3px dashed #CBD5E1;
  border-radius: 16px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.drop-zone:hover,
.drop-zone.dragging {
  border-color: #3B82F6;
  background: #EFF6FF;
}

.file-input {
  display: none;
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  width: 64px;
  height: 64px;
  color: #3B82F6;
}

.drop-text {
  font-size: 16px;
  font-weight: 600;
  color: #475569;
}

.browse-button {
  color: #3B82F6;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
}

.drop-hint {
  font-size: 14px;
  color: #94A3B8;
}

.upload-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #F1F5F9;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3B82F6, #2563EB);
  transition: width 0.3s;
}

.progress-text {
  text-align: center;
  font-weight: 700;
  color: #3B82F6;
}

.folder-select {
  display: flex;
  align-items: center;
  gap: 12px;
}

.folder-select label {
  font-weight: 600;
  color: #475569;
}

.folder-input {
  flex: 1;
  padding: 10px;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
}

/* Library Section */
.library-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filters {
  display: flex;
  gap: 12px;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
}

.folder-filter {
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #F1F5F9;
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.media-item:hover {
  border-color: #3B82F6;
  transform: scale(1.05);
}

.media-item.selected {
  border-color: #3B82F6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
}

.media-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 12px 8px 8px;
  color: white;
}

.media-name {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-size {
  font-size: 10px;
  opacity: 0.8;
}

.checkmark {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: #3B82F6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}

.empty-state {
  padding: 80px 40px;
  text-align: center;
  color: #94A3B8;
  font-weight: 600;
  font-size: 16px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 2px solid #F1F5F9;
}

.page-button {
  padding: 8px 16px;
  border: 2px solid #E2E8F0;
  background: white;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.page-button:hover:not(:disabled) {
  border-color: #3B82F6;
  color: #3B82F6;
}

.page-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: #475569;
}

/* URL Section */
.url-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}

.url-label {
  font-weight: 700;
  font-size: 16px;
  color: #1E293B;
}

.url-direct-input {
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 10px;
  font-size: 14px;
}

.url-submit-button {
  padding: 12px 24px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.url-submit-button:hover {
  background: #2563EB;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 2px solid #F1F5F9;
}

.cancel-button {
  padding: 12px 24px;
  background: #F1F5F9;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #E2E8F0;
}

.confirm-button {
  padding: 12px 24px;
  background: #10B981;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-button:hover {
  background: #059669;
}
</style>
